import { useState, useRef, useCallback, useEffect } from 'react'
import * as Cesium from 'cesium'

// ── 거리 (측지선) ────────────────────────────────────────────────────
function geodesicDistance(a: Cesium.Cartesian3, b: Cesium.Cartesian3): number {
  const ell = Cesium.Ellipsoid.WGS84
  return new Cesium.EllipsoidGeodesic(
    ell.cartesianToCartographic(a),
    ell.cartesianToCartographic(b),
  ).surfaceDistance
}

// ── 폴리곤 면적 (로컬 ENU 평면 투영 → 신발끈 공식) ─────────────────
function flatPolygonArea(pts: Cesium.Cartesian3[]): number {
  if (pts.length < 3) return 0
  const center = Cesium.BoundingSphere.fromPoints(pts).center
  const inv = Cesium.Matrix4.inverseTransformation(
    Cesium.Transforms.eastNorthUpToFixedFrame(center),
    new Cesium.Matrix4(),
  )
  const local = pts.map(p => Cesium.Matrix4.multiplyByPoint(inv, p, new Cesium.Cartesian3()))
  let area = 0
  for (let i = 0; i < local.length; i++) {
    const j = (i + 1) % local.length
    area += local[i].x * local[j].y - local[j].x * local[i].y
  }
  return Math.abs(area) / 2
}

// ── 포인트 인 폴리곤 (2D 레이캐스팅) ────────────────────────────────
function pointInPoly2D(p: [number, number], poly: [number, number][]): boolean {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j]
    if (((yi > p[1]) !== (yj > p[1])) &&
        p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

// ── 메시 기반 부피 계산 ──────────────────────────────────────────────
// 폴리곤 내부를 N×N 격자로 샘플링하여 sampleHeightMostDetailed로 높이를
// 획득, 최저점 기준 기둥 부피 합산
async function calcMeshVolume(
  pts: Cesium.Cartesian3[],
  scene: Cesium.Scene,
): Promise<number | null> {
  if (!scene.sampleHeightSupported) return null

  const cartos = pts.map(p => Cesium.Cartographic.fromCartesian(p))

  // 폴리곤 중심에서 아래 방향으로 ray를 쏴 3D Tileset 존재 여부를 먼저 확인
  const centerLon = (Math.min(...cartos.map(c => c.longitude)) + Math.max(...cartos.map(c => c.longitude))) / 2
  const centerLat = (Math.min(...cartos.map(c => c.latitude)) + Math.max(...cartos.map(c => c.latitude))) / 2
  const rayOrigin = Cesium.Cartesian3.fromRadians(centerLon, centerLat, 10_000)
  const downDir = Cesium.Cartesian3.negate(
    Cesium.Cartesian3.normalize(rayOrigin, new Cesium.Cartesian3()),
    new Cesium.Cartesian3(),
  )
  const hit = (scene as any).pickFromRay(new Cesium.Ray(rayOrigin, downDir))
  if (!(hit?.object?.primitive instanceof Cesium.Cesium3DTileset)) return null
  const poly2d: [number, number][] = cartos.map(c => [c.longitude, c.latitude])

  const lons = poly2d.map(([lon]) => lon)
  const lats = poly2d.map(([, lat]) => lat)
  const minLon = Math.min(...lons), maxLon = Math.max(...lons)
  const minLat = Math.min(...lats), maxLat = Math.max(...lats)

  const N = 30
  const sampleCartos: Cesium.Cartographic[] = []
  for (let i = 0; i < N; i++) {
    for (let j = 0; j < N; j++) {
      const lon = minLon + (i + 0.5) * (maxLon - minLon) / N
      const lat = minLat + (j + 0.5) * (maxLat - minLat) / N
      if (pointInPoly2D([lon, lat], poly2d)) {
        sampleCartos.push(new Cesium.Cartographic(lon, lat))
      }
    }
  }
  if (sampleCartos.length === 0) return null

  let sampled: (Cesium.Cartographic | undefined)[]
  try {
    sampled = await scene.sampleHeightMostDetailed(sampleCartos)
  } catch {
    return null
  }

  const validHeights = sampled
    .map(c => c?.height)
    .filter((h): h is number => h !== undefined && isFinite(h))
  if (validHeights.length < 3) return null

  const hBase = Math.min(...validHeights)

  // 격자 셀 넓이 = 바운딩박스 넓이 / (N²)
  const bboxPts = [
    Cesium.Cartesian3.fromRadians(minLon, minLat),
    Cesium.Cartesian3.fromRadians(maxLon, minLat),
    Cesium.Cartesian3.fromRadians(maxLon, maxLat),
    Cesium.Cartesian3.fromRadians(minLon, maxLat),
  ]
  const cellArea = flatPolygonArea(bboxPts) / (N * N)

  const vol = sampled.reduce((s, c) => {
    const h = c?.height
    if (h === undefined || !isFinite(h)) return s
    return s + Math.max(0, h - hBase)
  }, 0) * cellArea

  return vol
}

// ── 포맷 ─────────────────────────────────────────────────────────────
export function fmtDist(m: number) {
  return m >= 1000 ? `${(m / 1000).toFixed(3)} km` : `${m.toFixed(1)} m`
}
export function fmtArea(m2: number) {
  return m2 >= 1_000_000 ? `${(m2 / 1_000_000).toFixed(4)} km²` : `${m2.toFixed(1)} m²`
}
export function fmtVol(m3: number) {
  if (m3 >= 1_000_000_000) return `${(m3 / 1_000_000_000).toFixed(4)} km³`
  if (m3 >= 1_000_000) return `${(m3 / 1_000_000).toFixed(3)} Mm³`
  return `${m3.toFixed(1)} m³`
}

export type MeasureTool = 'distance' | 'area' | 'volume'

export function useMeasurement(
  viewerRef: { readonly current: Cesium.Viewer | null },
  hasMesh = false,
) {
  const [activeTool, setActiveTool] = useState<MeasureTool | null>(null)
  const [measureResult, setMeasureResult] = useState<string | null>(null)
  const [showVolumeInput, setShowVolumeInput] = useState(false)
  const [volumeHeight, setVolumeHeight] = useState('10')
  const [pointCount, setPointCount] = useState(0)
  const [isCalcMesh, setIsCalcMesh] = useState(false)

  const activeToolRef = useRef<MeasureTool | null>(null)
  const hasMeshRef = useRef(hasMesh)
  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null)
  const pointsRef = useRef<Cesium.Cartesian3[]>([])
  const entitiesRef = useRef<Cesium.Entity[]>([])
  const previewLineRef = useRef<Cesium.Entity | null>(null)
  const previewPosRef = useRef<Cesium.Cartesian3[]>([])
  const polygonAreaRef = useRef(0)

  useEffect(() => { activeToolRef.current = activeTool }, [activeTool])
  useEffect(() => { hasMeshRef.current = hasMesh }, [hasMesh])

  useEffect(() => () => {
    if (handlerRef.current && !handlerRef.current.isDestroyed()) {
      handlerRef.current.destroy()
    }
  }, [])

  const removeEntities = useCallback(() => {
    const v = viewerRef.current
    if (v && !v.isDestroyed()) {
      entitiesRef.current.forEach(e => v.entities.remove(e))
      if (previewLineRef.current) {
        v.entities.remove(previewLineRef.current)
        previewLineRef.current = null
      }
    }
    entitiesRef.current = []
    pointsRef.current = []
    previewPosRef.current = []
    setPointCount(0)
  }, [viewerRef])

  const destroyHandler = useCallback(() => {
    if (handlerRef.current && !handlerRef.current.isDestroyed()) {
      handlerRef.current.destroy()
      handlerRef.current = null
    }
    const v = viewerRef.current
    if (v && !v.isDestroyed()) v.scene.canvas.style.cursor = ''
    activeToolRef.current = null
    setActiveTool(null)
  }, [viewerRef])

  const clearMeasure = useCallback(() => {
    removeEntities()
    setMeasureResult(null)
    setShowVolumeInput(false)
    setIsCalcMesh(false)
  }, [removeEntities])

  const stopMeasure = useCallback(() => {
    clearMeasure()
    destroyHandler()
  }, [clearMeasure, destroyHandler])

  const finishMeasure = useCallback(() => {
    const tool = activeToolRef.current
    const v = viewerRef.current
    if (!tool || !v || v.isDestroyed()) return

    const pts = pointsRef.current
    if (tool === 'distance' ? pts.length < 2 : pts.length < 3) return

    if (previewLineRef.current) {
      v.entities.remove(previewLineRef.current)
      previewLineRef.current = null
    }

    const addE = (e: Cesium.Entity) => { v.entities.add(e); entitiesRef.current.push(e) }

    if (tool === 'distance') {
      const total = pts.reduce((s, p, i) => i === 0 ? s : s + geodesicDistance(pts[i - 1], p), 0)
      setMeasureResult(`총 거리: ${fmtDist(total)}`)
      destroyHandler()
      return
    }

    // 면적 / 부피 공통: 폴리곤 닫기
    const color = Cesium.Color.fromCssColorString('#16a34a')
    addE(new Cesium.Entity({
      polyline: {
        positions: [pts[pts.length - 1], pts[0]],
        width: 2,
        material: new Cesium.ColorMaterialProperty(color),
      },
    }))
    addE(new Cesium.Entity({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(pts),
        material: color.withAlpha(0.15),
      },
    }))
    const area = flatPolygonArea(pts)
    polygonAreaRef.current = area
    const center = Cesium.BoundingSphere.fromPoints(pts).center
    addE(new Cesium.Entity({
      position: center,
      label: {
        text: fmtArea(area),
        font: '12px sans-serif',
        fillColor: Cesium.Color.WHITE,
        backgroundColor: Cesium.Color.fromCssColorString('#15803d').withAlpha(0.85),
        showBackground: true,
        backgroundPadding: new Cesium.Cartesian2(5, 3),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    }))
    setMeasureResult(`면적: ${fmtArea(area)}`)
    destroyHandler()

    if (tool === 'volume') {
      if (hasMeshRef.current) {
        // 메시 기반 부피 계산 (비동기)
        const ptSnapshot = [...pts]
        setIsCalcMesh(true)
        calcMeshVolume(ptSnapshot, v.scene).then(vol => {
          setIsCalcMesh(false)
          if (vol !== null) {
            setMeasureResult(`면적: ${fmtArea(area)}\n부피 (메시): ${fmtVol(vol)}`)
          } else {
            // 샘플링 실패 시 수동 입력으로 폴백
            setShowVolumeInput(true)
          }
        })
      } else {
        setShowVolumeInput(true)
      }
    }
  }, [viewerRef, destroyHandler])

  const finishRef = useRef(finishMeasure)
  useEffect(() => { finishRef.current = finishMeasure }, [finishMeasure])

  const stopMeasureRef = useRef(stopMeasure)
  useEffect(() => { stopMeasureRef.current = stopMeasure }, [stopMeasure])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopMeasureRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const computeVolume = useCallback(() => {
    const h = parseFloat(volumeHeight)
    if (isNaN(h) || h <= 0) return
    const vol = polygonAreaRef.current * h
    setMeasureResult(`면적: ${fmtArea(polygonAreaRef.current)}\n부피: ${fmtVol(vol)}`)
    setShowVolumeInput(false)
  }, [volumeHeight])

  const startMeasure = useCallback((tool: MeasureTool) => {
    if (activeToolRef.current === tool) { stopMeasure(); return }

    clearMeasure()
    destroyHandler()

    const v = viewerRef.current
    if (!v || v.isDestroyed()) return

    activeToolRef.current = tool
    setActiveTool(tool)
    pointsRef.current = []
    v.scene.canvas.style.cursor = 'crosshair'

    const drawColor = tool === 'distance'
      ? Cesium.Color.fromCssColorString('#2563eb')
      : Cesium.Color.fromCssColorString('#16a34a')

    const handler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas)
    handlerRef.current = handler

    const getPos = (win: Cesium.Cartesian2): Cesium.Cartesian3 | undefined => {
      if (v.scene.pickPositionSupported) {
        const p = v.scene.pickPosition(win)
        if (Cesium.defined(p)) return p
      }
      return v.camera.pickEllipsoid(win, v.scene.globe.ellipsoid) ?? undefined
    }

    const addE = (e: Cesium.Entity): Cesium.Entity => {
      v.entities.add(e); entitiesRef.current.push(e); return e
    }

    handler.setInputAction((evt: { position: Cesium.Cartesian2 }) => {
      const pos = getPos(evt.position)
      if (!pos) return
      const pts = pointsRef.current
      const idx = pts.length
      pts.push(pos)
      setPointCount(pts.length)

      addE(new Cesium.Entity({
        position: pos,
        point: {
          pixelSize: idx === 0 ? 10 : 7,
          color: idx === 0 ? Cesium.Color.YELLOW : Cesium.Color.WHITE,
          outlineColor: drawColor,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      }))

      if (idx > 0) {
        const prev = pts[idx - 1]
        addE(new Cesium.Entity({
          polyline: {
            positions: [prev, pos],
            width: 2,
            material: new Cesium.ColorMaterialProperty(drawColor),
          },
        }))
        if (tool === 'distance') {
          const segDist = geodesicDistance(prev, pos)
          const total = pts.reduce((s, p, i) => i === 0 ? s : s + geodesicDistance(pts[i - 1], p), 0)
          const mid = Cesium.Cartesian3.midpoint(prev, pos, new Cesium.Cartesian3())
          addE(new Cesium.Entity({
            position: mid,
            label: {
              text: fmtDist(segDist),
              font: '11px sans-serif',
              fillColor: Cesium.Color.WHITE,
              backgroundColor: Cesium.Color.fromCssColorString('#1e3a8a').withAlpha(0.85),
              showBackground: true,
              backgroundPadding: new Cesium.Cartesian2(5, 3),
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -6),
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
          }))
          setMeasureResult(`총 거리: ${fmtDist(total)}`)
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    handler.setInputAction((evt: { endPosition: Cesium.Cartesian2 }) => {
      const pts = pointsRef.current
      if (pts.length === 0) return
      const pos = getPos(evt.endPosition)
      if (!pos) return
      previewPosRef.current = [pts[pts.length - 1], pos]
      if (!previewLineRef.current) {
        previewLineRef.current = v.entities.add(new Cesium.Entity({
          polyline: {
            positions: new Cesium.CallbackProperty(() => previewPosRef.current, false),
            width: 1.5,
            material: new Cesium.PolylineDashMaterialProperty({
              color: drawColor.withAlpha(0.5),
              dashLength: 8,
            }),
          },
        }))
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    handler.setInputAction(() => {
      finishRef.current()
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK)

  }, [clearMeasure, destroyHandler, stopMeasure, viewerRef])

  const canFinish = activeTool !== null && (
    (activeTool === 'distance' && pointCount >= 2) ||
    ((activeTool === 'area' || activeTool === 'volume') && pointCount >= 3)
  )

  return {
    activeTool,
    measureResult,
    showVolumeInput,
    volumeHeight,
    setVolumeHeight,
    canFinish,
    isCalcMesh,
    startMeasure,
    stopMeasure,
    finishMeasure,
    computeVolume,
  }
}
